<template>
  <v-container>
    <ExtractReportGenerator
      :disabled="isGenerateExtractDisabled"
      @generate-extract-requested="generateReport"
    >
      <v-container class="report-jda d-flex flex-column pa-0 ma-0" fluid>
        <v-row no-gutters>
          <v-col class="colspan">
            <div
              v-if="isScenarioInputIncomplete && !missingAttributes.length"
              class="validation-message"
            >
              {{ tkey('extractValidation.incompleteInputs') }}
            </div>
            <div v-if="missingAttributes.length" class="validation-message">
              {{ tkey('extractValidation.missingAttributes') }}
              <ul>
                <li v-for="item in missingAttributes" :key="item">
                  {{ item }}
                </li>
              </ul>
              {{ tkey('extractValidation.addAttributesInstruction') }}
            </div>
            <ReportInputItem class="mb-4" :title="tkey('extractName')">
              <AssortmentTextField
                v-model="extractName"
                :disabled="isEditingDisabled"
                variant="filled"
                bg-color="white"
                :placeholder="tkey('chooseAnExtractName')"
              />
            </ReportInputItem>
            <ReportInputItem v-if="requiredAttributesForExtract.length">
              <template v-if="usesPresentationCategoryId" #title>
                <div class="d-flex mb-2">
                  <span class="mr-1 font-weight-bold">{{ tkey('category') }} </span>
                  <span> {{ tkey('categoryHelperText') }}</span>
                </div>
              </template>
              <v-radio-group v-model="category" class="jda-radio-group">
                <v-radio v-for="item in categories" :key="item" :label="`${item}`" :value="item" />
              </v-radio-group>
            </ReportInputItem>
          </v-col>
          <v-col class="colspan" />
        </v-row>
      </v-container>
    </ExtractReportGenerator>
  </v-container>
</template>

<script>
// Composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useDayjs } from '@/composables/use-dayjs';
import { usePermission } from '@/composables/use-permission';

// Stores
import { useContextStore } from '@/stores/context';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useExtractsStore } from '@/stores/extracts';

// Enums
import clientEvents from '@enums/client-events';
import extractTypes from '@enums/extract-types';
import sectionStatuses from '@enums/section-status';

import { difference, get, includes, isEmpty, isEqual, size, uniq } from 'lodash';
import { mapActions, mapState, mapGetters } from 'pinia';

export default {
  setup() {
    const { tkey } = useInternationalization(
      'extract.reports.storeExecutionPlanning.storeExecutionPlanningReport'
    );
    const { getUTCNow, dayjs } = useDayjs();
    const { canEditProductStoreExtracts, canEditStoreExecutionPlanning } = usePermission();

    return { tkey, getUTCNow, dayjs, canEditProductStoreExtracts, canEditStoreExecutionPlanning };
  },
  data() {
    return {
      extractName: null,
      category: null,
      selectedAttribute: null,
      missingAttributes: [],
      categories: [],
      presentationCategoryAttrName: 'Presentation_Category',
    };
  },

  computed: {
    ...mapState(useWorkpackagesStore, ['selectedWorkpackage']),
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useContextStore, ['clientConfig', { dateFormats: 'getDateFormats' }]),
    ...mapGetters(useScenariosStore, ['selectedScenarioStatus']),

    isGenerateExtractDisabled() {
      // Button "Generate Extract" enabled when PAE status is complete and all required attributes are specified
      return (
        this.isScenarioInputIncomplete ||
        !isEmpty(this.missingAttributes) ||
        !this.canEditProductStoreExtracts
      );
    },

    isScenarioInputIncomplete() {
      return this.selectedScenarioStatus.inputs.status !== sectionStatuses.complete;
    },

    isEditingDisabled() {
      return !this.canEditStoreExecutionPlanning;
    },

    requiredAttributesForExtract() {
      return get(this.clientConfig, 'features.storeExecutions.requiredAttributes', []);
    },

    usesPresentationCategoryId() {
      return this.requiredAttributesForExtract.includes(this.presentationCategoryAttrName);
    },
  },

  created() {
    this.setSelectedStep('report-jda');
    this.validateRequiredAttributesForExtract();
  },

  events: {
    [clientEvents.extracts.generateExtractRequested]() {
      this.generateReport();
    },
  },

  methods: {
    ...mapActions(useExtractsStore, ['setSelectedStep', 'generateStoreExecutionsExtracts']),
    ...mapActions(useScenarioProductsStore, ['fetchScenarioProducts']),

    validateRequiredAttributesForExtract() {
      let presentationCategoryId;

      // Get active scenario required attribute list for extract
      const scenarioAttributesForExtract = this.selectedScenario.customAttributes
        .filter(attribute => {
          if (
            this.usesPresentationCategoryId &&
            attribute.name === this.presentationCategoryAttrName
          ) {
            presentationCategoryId = attribute.id;
          }
          return includes(this.requiredAttributesForExtract, attribute.name);
        })
        .map(attribute => attribute.name);

      // If active scenario has not all list of required attributes for extract, we searching for missing ones
      // Missing attribute list is used for validation message
      if (!isEqual(size(scenarioAttributesForExtract), size(this.requiredAttributesForExtract))) {
        this.missingAttributes = difference(
          this.requiredAttributesForExtract,
          scenarioAttributesForExtract
        );
      }

      if (isEmpty(this.missingAttributes)) {
        this.setCategories(presentationCategoryId);
      }
    },

    async setCategories(presentationCategoryId) {
      const scenarioProducts = await this.fetchScenarioProducts({
        params: { pick: ['customAttributes'] },
      });

      // Searching for all unique Presentation_Category attribute values
      scenarioProducts.forEach(product => {
        product.customAttributes.forEach(attribute => {
          if (attribute.id === presentationCategoryId && attribute.value) {
            this.categories.push(attribute.value);
          }
        });
      });

      this.categories = uniq(this.categories);

      if (this.categories.length) this.category = this.categories[0];
    },

    async generateReport() {
      await this.generateStoreExecutionsExtracts({
        filenameOnDownload:
          this.extractName ||
          this.$t('extract.reports.defaultExtractFileName', [
            this.dayjs().format(this.dateFormats.longWithTime),
          ]),
        extractType: extractTypes.STORE_EXECUTION,
        extractDatetime: this.getUTCNow().toDate(),
        fileId: null,
        category: this.category,
        requiredAttributes: this.requiredAttributesForExtract,
      });
      this.globalEmit(clientEvents.extracts.extractGenerated);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
.report-jda {
  .validation-message {
    color: $assortment-span-error-colour;
    font-weight: bold;
    font-size: 1.2rem;
  }

  .jda-radio-group {
    :deep(.v-input__control) {
      width: 100%;
    }

    .v-radio {
      display: flex;
      justify-content: space-between;
      flex-direction: row-reverse;
      border: 1px solid $assortment-border-colour;
      border-radius: 3px;
      padding: 0 8px;
      margin-right: 0;

      &.v-item--active {
        border-color: $assortment-primary-colour;
      }

      :deep(.v-label) {
        width: 100%;
        color: black;
      }

      :deep(.v-input--selection-controls__input) {
        margin-right: 0;
      }
    }
  }
}
</style>
